import { useQuery, useQueryClient } from "@tanstack/react-query";
import { optionsDualBox, OptionSelect } from "Common/Components";
import { useSessionStore } from "Common/Stores";
import { NotificationType, showNotifications } from "helpers/toas_notification";
import {
  AssignedPermission,
  Option,
  CreatePermissions,
  Permission,
} from "interfaces";
import { layoutStore } from "Layout/VerticalLayout/store/layoutMenu.store";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { deleteArrayPermissions, GetMenus, GetRolesByEmpresa } from "services";
import { getPermissionsByRoleAndMenu, postArrayPermissions } from "services";
import { useStore } from "zustand";

export const usePermissionsAssigned = () => {
  const { t } = useTranslation();
  const { session } = useSessionStore();
  const queryClient = useQueryClient();

  const [CompanyId, setCompanyId] = useState(
    session?.employe != null ? session?.employe.branch.company.id : 0
  );

  const [rolesOptions, setRolesOptions] = useState<OptionSelect[]>([]);
  const [menuOptions, setMenuOptions] = useState<OptionSelect[]>([]);

  const [rolSelected, setRolSelected] = useState<string>("0");
  const [menuSelected, setMenuSelected] = useState<string>("0");

  const [permissionsAssigned, setPermissionsAssigned] = useState<string[]>([]);
  const [permissionsUnassigned, setPermissionsUnassigned] = useState<
    optionsDualBox[]
  >([]);

  // ...
  const {
    triggerRefreshMenu,
  } = useStore(layoutStore);

  const { data: roles } = useQuery({
    queryKey: ["roles", CompanyId],
    queryFn: () => GetRolesByEmpresa(CompanyId.toString()),
    enabled: CompanyId !== 0,
  });

  const {
    data: menus,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["menus"],
    queryFn: GetMenus,
  });

  const {
    data: permissions,
    isLoading: isLoadingPermissions,
    isError: isErrorPermissions,
    error: errorPermissions,
  } = useQuery({
    queryKey: ["permissions", rolSelected, menuSelected],
    queryFn: () =>
      getPermissionsByRoleAndMenu(Number(rolSelected), Number(menuSelected)),
    enabled: rolSelected !== "0" && menuSelected !== "0",
  });

  useEffect(() => {
    if (roles) {
      setRolesOptions(
        roles.data.map((role) => {
          return {
            label: role.description,
            value: role.id.toString(),
          };
        })
      );
    }
  }, [roles]);

  useEffect(() => {
    if (menus) {
      setMenuOptions(
        menus.data.map((menu) => {
          return {
            label: t(`menu.${menu.description}`, menu.description),
            value: menu.id.toString(),
          };
        })
      );
    }
  }, [menus, t]);

  useEffect(() => {
    if (permissions) {
      setPermissionsAssigned(
        permissions.assignedPermissions.map(
          (permission) => permission.option.description
        )
      );

      const permissionsNotAssignet = permissions.unassignedOptions.map(
        (permission) => {
          return {
            label: t(`menu.${permission.description}`, permission.description),
            value: permission.description,
          };
        }
      );

      permissionsNotAssignet.push(
        ...permissions.assignedPermissions.map((permission) => ({
          label: t(`menu.${permission.option.description}`, permission.option.description),
          value: permission.option.description,
        }))
      );

      setPermissionsUnassigned(permissionsNotAssignet);
    }
  }, [permissions, t]);

  const changePermissionsAssigned = (listPermissions: string[]) => {
    //Validated if the permissions are added or removed
    const addedPermissions = listPermissions.filter(
      (permissions) => !permissionsAssigned.includes(permissions)
    );
    const removedPermissions = permissionsAssigned.filter(
      (permissions) => !listPermissions.includes(permissions)
    );

    //execute the logic to add or remove permissions
    if (addedPermissions.length > 0) {
      createNewPermission(addedPermissions);
    }

    if (removedPermissions.length > 0) {
      removePermissions(removedPermissions);
    }

    setPermissionsAssigned(listPermissions);
  };

  //Function to find the permission to add or remove from the list of permissions
  const findPermissionToAddorRemove = (
    key: string,
    list: AssignedPermission[] | Option[]
  ) => {
    if (list.length === 0) return null;

    if (list[0].hasOwnProperty("option")) {
      return (list as AssignedPermission[]).find(
        (permission) => permission.option.description === key
      );
    } else {
      return (list as Option[]).find(
        (permission) => permission.description === key
      );
    }
  };

  const createNewPermission = async (permissionsAdded: string[]) => {
    //Find the permission object to add
    const permissionsFind = permissionsAdded.map((permissionAdd) => {
      const findPermissions = permissions?.unassignedOptions
        ? findPermissionToAddorRemove(
            permissionAdd,
            permissions.unassignedOptions
          )
        : null;

      return findPermissions;
    });

    const createPayload: Permission[] = permissionsFind.map((permission) => {
      return {
        optionid: permission?.id,
        roleid: Number(rolSelected),
      } as Permission;
    });

    const payload: CreatePermissions = {
      permissions: createPayload,
    };

    try {
      const response = await postArrayPermissions(payload);

      if (response.code === "200") {
        triggerRefreshMenu();
        showNotifications(
          NotificationType.SUCCESS,
          t("notificationsMessages.successAdd")
        );

        //Update the permissions list

        queryClient.invalidateQueries({
          queryKey: ["permissions", rolSelected, menuSelected],
        });
      } else {
        showNotifications(
          NotificationType.ERROR,
          t("notificationsMessages.error" + response.message)
        );
      }
    } catch (error) {
      showNotifications(
        NotificationType.ERROR,
        t("notificationsMessages.error" + error)
      );
    }
  };

  const removePermissions = async (permissionsRemoved: string[]) => {
    //Find the permission object to add
    const permissionsFind = permissionsRemoved.map((permissionAdd) => {
      const findPermissions = permissions?.assignedPermissions
        ? findPermissionToAddorRemove(
            permissionAdd,
            permissions.assignedPermissions
          )
        : null;

      return findPermissions;
    });

    const createPayload: number[] = permissionsFind.map((permission) => {
      return permission?.id as number;
    });

    const payload = {
      idsPermisos: createPayload,
    };

    try {
      const response = await deleteArrayPermissions(payload);

      if (response.code === "200") {
        triggerRefreshMenu();
        showNotifications(
          NotificationType.SUCCESS,
          t("notificationsMessages.success")
        );

        //Update the permissions list

        queryClient.invalidateQueries({
          queryKey: ["permissions", rolSelected, menuSelected],
        });
      } else {
        showNotifications(
          NotificationType.ERROR,
          t("notificationsMessages.error") + response.message
        );
      }
    } catch (error) {
      showNotifications(
        NotificationType.ERROR,
        t("notificationsMessages.error") + error
      );
    }
  };

  return {
    rolesOptions,
    rolSelected,
    menuOptions,
    menuSelected,
    permissionsAssigned,
    permissionsUnassigned,
    setMenuSelected,
    setRolSelected,
    setPermissionsAssigned,
    setPermissionsUnassigned,
    changePermissionsAssigned,
  };
};